import {Modal, StyleSheet, Text, View} from "react-native";
import {Color} from "@/lib/styles";
import Page from "@/lib/components/Page";
import Center from "@/lib/components/Center";
import {useSession} from "@/lib/auth/auth";
import {AccountEditRequest} from "@/lib/types";
import {fetchBackendNoContent} from "@/lib/backend";
import ErrorPage from "@/lib/components/ErrorPage";
import SpinnerPage from "@/lib/components/SpinnerPage";
import Nav from "@/lib/components/Nav";
import {useMemo, useState} from "react";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Editable from "@/lib/components/Editable";
import Colorpicker from "@/lib/components/Colorpicker";
import Split from "@/lib/components/Split";
import Avatar from "@/lib/components/Avatar";
import Icon from "@/lib/components/Icon";
import {useRouter} from "expo-router";
import {colorName} from "@/lib/color";
import ActionButton from "@/lib/components/ActionButton";
import Header from "@/lib/components/Header";
import {useAccount} from "@/lib/components/AccountProvider";

type EditableComponent = null | "color";

export default function Index() {

    const {session} = useSession();
    const router = useRouter();

    const [isEditing, setIsEditing] = useState<EditableComponent>(null);
    const [saveError, setSaveError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);

    const {account, accountError, accountLoading, refreshAccount} = useAccount();

    const colorNameMemo = useMemo(() => {
        return colorName(account?.avatar.color!);
    }, [account])

    if (accountError) {
        return <ErrorPage error={accountError}/>
    } else if (accountLoading || !account) {
        return <SpinnerPage/>;
    }

    if (busy) return <SpinnerPage/>;

    const saveProfile = async (changes: AccountEditRequest) => {
        setSaveError(null);
        setBusy(true);
        fetchBackendNoContent(`/account`, "PATCH", session, changes).then(async () => {
            await refreshAccount();
            setIsEditing(null);
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setIsEditing(null);
            setBusy(false);
        });
    }

    const cameraSize = 32;

    return (
        <Page scrollable={true}>
            <View style={[styles.card, {backgroundColor: account.avatar.color}]}>
                <Split left={
                    <>
                        <Avatar avatar={account.avatar} size={40}/>
                        <Text selectable={false} style={[styles.username, {color: account.avatar.contrast_color}]}>{account.avatar.username}</Text>
                    </>
                } right={
                    <></>
                }/>
                <Editable
                    color={account.avatar.contrast_color}
                    style={{
                        flexDirection: "column",
                        alignItems: "center",
                        alignContent: "center",
                    }}
                    onPress={() => {
                        router.navigate("/u/account/picture");
                    }}
                >
                    <Avatar avatar={account.avatar} pressable={false} size={144}/>
                    <View style={{
                        position: "absolute",
                        left: "34%",
                        top: "77%",
                        height: cameraSize,
                        width: cameraSize,
                        borderRadius: cameraSize,
                        backgroundColor: Color.Background,
                    }}>
                        <Icon kind={"camera"} color={Color.PrimaryText} size={cameraSize}/>
                    </View>
                </Editable>

                <Editable color={account.avatar.contrast_color} onPress={() => setIsEditing("color")}>
                    <Text style={[styles.editPart, {color: account.avatar.contrast_color}]}>Farge</Text>
                    <Text style={[styles.editValue, {color: account.avatar.contrast_color}]}>{colorNameMemo}</Text>
                </Editable>

            </View>

            {saveError && <ErrorMessage error={saveError}/>}

            <Nav>
                <ActionButton
                    title={"Ferdig"}
                    icon={"checkmark"}
                    onPress={async () => {
                        router.dismiss();
                    }}
                />
            </Nav>

            <Modal onRequestClose={() => setIsEditing(null)}
                   visible={!!isEditing}
                   allowSwipeDismissal={true}
                   presentationStyle={"pageSheet"}
                   animationType={"slide"}
            >
                <Page>
                    <Center>
                        <Header title={"Velg profilfarge"} center={true} marginBottom={16}/>
                        <Colorpicker color={account.avatar.color} onChange={async (color) => {
                            setIsEditing(null);
                            await saveProfile({color: color.color});
                        }}/>
                    </Center>
                </Page>
            </Modal>
        </Page>
    );
}

const styles = StyleSheet.create({
    editPart: {
        fontSize: 16,
        fontWeight: 600,
    },
    editValue: {
        fontSize: 16,
    },
    card: {
        flexDirection: "column",
        borderRadius: 16,
        padding: 6,
        gap: 8,
    },
    username: {
        fontSize: 16,
        fontWeight: 600,
    },
});